import type { SelectChangeEvent } from "@mui/material";
import type { FieldValidator, FormikValues } from "formik";
import { useField, useFormikContext } from "formik";
import { forwardRef, useCallback, useImperativeHandle, useMemo } from "react";
import { FormattedMessage } from "react-intl";

import type {
  BaseSelectProps as StandardBaseSelectProps,
  SelectProps as StandardSelectProps,
} from "@citrine/atoms/select";
import {
  EMPTY_SELECT_OPTION_VALUE,
  Select as StandardSelect,
} from "@citrine/atoms/select";
import { sleep } from "@citrine/utils/helpers";
import type { Paths } from "@citrine/utils/types";

import { useFormConfiguration } from "./form";
import { useValidateOnChange } from "./utils";

export type { SelectOption } from "@citrine/atoms/select";

type SingleOrMultiSelect<
  Shape extends FormikValues,
  ValueType extends string | undefined = string,
> =
  | {
      name: Paths<Shape, ValueType>;
      multiple?: false;
      none?: boolean | string;
    }
  | {
      name: Paths<Shape, ValueType[]>;
      multiple: true;
      none?: false;
    };

/**
 * In a perfect world, I would be able to infer the ValueType from options
 * However, TS allows us to _either_ explicitly pass generic params or infer,
 * but not both.
 * And we _have_ to define Shape, so we cannot infer ValueType
 *
 * more context: https://github.com/Microsoft/TypeScript/pull/26349
 */
interface BaseSelectProps<
  ValueType extends string | undefined = string,
> extends Omit<StandardSelectProps<ValueType>, "name" | "children" | "value"> {
  options: NonNullable<StandardSelectProps<ValueType>["options"]>;
  none?: React.ReactNode;
  empty?: React.ReactNode;
}

export type SelectProps<
  Shape extends FormikValues,
  ValueType extends string | undefined = string,
> = BaseSelectProps<ValueType> &
  SingleOrMultiSelect<Shape, ValueType> & { validateOnChange?: boolean } & {
    validate?: FieldValidator;
  };

export interface SelectRef<ValueType extends string | undefined = string> {
  setValue: (value: ValueType, shouldValidate?: boolean) => void;
}

function SelectImpl<Shape extends FormikValues, ValueType extends string>(
  {
    name,
    options: optionsInput,
    none,
    empty,
    onChange,
    onBlur,
    disabled,
    validateOnChange: validateOnChangeProp,
    multiple,
    validate,
    ...props
  }: SelectProps<Shape, ValueType>,
  ref: React.ForwardedRef<SelectRef<ValueType>>
) {
  type InternalValueType = StandardSelectProps<ValueType>["value"];

  const { id: formId, readonly } = useFormConfiguration();

  const [field, meta, helpers] = useField<InternalValueType>({
    name,
    validate,
  });
  const { isSubmitting, validateForm } = useFormikContext();

  const validateOnChange = useValidateOnChange(validateOnChangeProp);

  useImperativeHandle(
    ref,
    () => ({
      setValue: helpers.setValue,
    }),
    [helpers.setValue]
  );

  const options = useMemo(
    () =>
      [
        /* These are the values that we render as part of the dropdown */
        /* If they provide a "" value we display the message. We only want */
        /* this available in the single select */
        none
          ? {
              disabled: readonly,
              value: "" as ValueType,
              label: (
                <em>
                  {none === true ? (
                    <FormattedMessage id="components.form.select.none" />
                  ) : (
                    none
                  )}
                </em>
              ),
            }
          : null,
        /* If they provide no options then we show a formatted empty message */
        empty && optionsInput.length === 0
          ? {
              value: EMPTY_SELECT_OPTION_VALUE as ValueType,
              disabled: true,
              label: (
                <em>
                  {empty === true ? (
                    <FormattedMessage id="components.form.select.empty" />
                  ) : (
                    empty
                  )}
                </em>
              ),
            }
          : null,
        ...optionsInput.map((option) =>
          typeof option === "object"
            ? {
                ...option,
                disabled: option.disabled || readonly,
              }
            : {
                disabled: readonly,
                value: option,
              }
        ),
      ].filter(Boolean),
    [empty, none, optionsInput, readonly]
  );

  const inputProps = useMemo(
    () => ({
      ...props.inputProps,
      form: formId,
    }),
    [formId, props.inputProps]
  );

  const onBlurCallback = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(e);
      field.onBlur(e);
    },
    [field, onBlur]
  );

  const onChangeCallback = useCallback(
    async (e: SelectChangeEvent<InternalValueType>, c: React.ReactNode) => {
      onChange?.(e, c);
      field.onChange(e);
      if (validateOnChange) {
        // await to allow all form changes to propagate prior to validation
        await sleep(0);
        validateForm();
      }
    },
    [field, onChange, validateForm, validateOnChange]
  );

  return (
    <StandardSelect<ValueType>
      {...(props as StandardBaseSelectProps)}
      value={field.value}
      multiple={multiple} // not part of StandardBaseSelectProps so separate it out
      name={name}
      disabled={disabled || isSubmitting}
      error={meta.touched ? meta.error : undefined}
      inputProps={inputProps}
      onBlur={onBlurCallback}
      onChange={onChangeCallback}
      options={options}
    />
  );
}

export const Select = forwardRef(SelectImpl) as <
  Shape extends FormikValues = FormikValues,
  ValueType extends string | undefined = string,
>(
  props: SelectProps<Shape, ValueType> & {
    ref?: React.ForwardedRef<SelectRef<ValueType>>;
  }
) => ReturnType<typeof SelectImpl>;
